import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, forkJoin, map, of, switchMap } from 'rxjs';

import { DataSpaceModel } from '@gfe/core/models/dataspace/data-space.model';
import { TransformerInfo } from '@gfe/core/models/transformers.model';
import { CsvTransformerService } from '@gfe/core/services/csv-transformer/csv-transformer.service';
import { CsvTransformerRegistryService } from '@gfe/core/services/user-models-registry/csv-transformer-registry.service';
import { isNil } from '@gfe/core/utils/shared-utils';

import { DataspaceService } from '@core/services/dataspace/dataspace.service';
import { EntitiesService } from '@core/services/entities/entities.service';

import * as DataspaceActions from './dataspace.actions';
import { selectDataspaceModel } from './dataspace.selectors';

@Injectable()
export class DataspaceEffects {
  private actions$ = inject(Actions);
  private readonly store = inject(Store);

  private readonly dataspaceService = inject(DataspaceService);
  private readonly entitiesService = inject(EntitiesService);
  private readonly csvTransformersService = inject(CsvTransformerService);
  private readonly csvTransformerRegistryService = inject(CsvTransformerRegistryService);

  fetchDataspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataspaceActions.fetchDataspace),
      // switchMap, not mergeMap: a fetch still in flight for the dataspace the user just
      // left would otherwise resolve last and overwrite the dataspace they switched to.
      switchMap(({ id }) =>
        forkJoin({
          dataspace: this.dataspaceService.getDataSpaceById$(id),
          availableEntities: this.entitiesService.getEntityMetadata$(id),
          transformers: this.csvTransformersService.getTransformers({ dataSpaceId: id })
        }).pipe(
          map(({ dataspace, availableEntities, transformers }) => {
            const transformerInterfaces = transformers.transformers.map(
              (transformerInfo: TransformerInfo) => {
                return this.csvTransformerRegistryService.getTransformerInterface(
                  transformerInfo
                );
              }
            );
            return DataspaceActions.switchToDataspace({
              dataspace: {
                id: dataspace.id,
                name: dataspace.name,
                description: dataspace.description,
                businessUnitId: dataspace.businessUnit.id
              },
              availableEntities: availableEntities,
              availableTransformers: transformerInterfaces,
              transformersInfo: transformers.transformers // Store raw TransformerInfo with schemas
            });
          }),
          catchError((error) => {
            console.error('DataspaceEffects: Error loading dataspace', id, error);
            return of(
              DataspaceActions.fetchDataspaceFailure({
                id,
                error: error?.error || error
              })
            );
          })
        )
      )
    )
  );

  refreshDataspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataspaceActions.refreshDataspace),
      concatLatestFrom(() => [this.store.select(selectDataspaceModel)]),
      map((action_ds) => action_ds[1]),
      filter((ds) => !isNil(ds)),
      map((ds: DataSpaceModel) => {
        return DataspaceActions.fetchDataspace({ id: ds.id });
      })
    )
  );
}
